import { Injectable } from '@angular/core';
import { CanActivate, Router, UrlTree } from '@angular/router';
import { Observable } from 'rxjs';
import { map, take } from 'rxjs/operators';
import { select, Store } from '@ngrx/store';
import { AppSelectionService } from 'src/app/services/app-selection.service';
import { AppState } from 'src/app/store/app-state.reducer';
import { selectAppState } from 'src/app/store/app-state.selectors';
import { setSelectedApplication } from 'src/app/store/app-state.actions';

/**
 * Gates the board on a single question: which application are we in?
 *
 * Deliberately SYNCHRONOUS — it only reads already-resolved state (the NgRx
 * store, falling back to the persisted choice in localStorage). Working out
 * which apps a user belongs to needs an authenticated API call, and that lives
 * on /select-app where it can show a spinner and an error. A guard can only
 * allow, block, or redirect, so a slow or empty call in here strands the user on
 * a blank screen with nothing to explain why.
 *
 * Authentication is handled one level up, by MsalGuard on the /dashboard route.
 * A parent route's guards fully resolve before a child's begin, so by the time
 * this runs the user is signed in — no racing, and nothing to wait on.
 */
@Injectable({
  providedIn: 'root'
})
export class BoardAccessGuard implements CanActivate {

  constructor(
    private store: Store<AppState>,
    private appSelection: AppSelectionService,
    private router: Router,
  ) {}

  canActivate(): Observable<boolean | UrlTree> {
    return this.store.pipe(
      select(selectAppState),
      take(1),
      map(({ selectedApplication, appList }) => {

        if (selectedApplication !== null) {
          return true;  // Already resolved for this session
        }

        // Returning user: rehydrate the persisted choice so a refresh or a deep
        // link goes straight in rather than bouncing through the picker. If the
        // app list happens to be loaded we sanity-check against it for free;
        // otherwise we trust it, because the appId is client-asserted either way
        // and the backend validates user-to-app membership and 403s on mismatch.
        const persistedId = this.appSelection.getPersistedAppId();
        if (persistedId !== null &&
            (appList.length === 0 || appList.some(app => app.applicationId === persistedId))) {
          this.store.dispatch(setSelectedApplication({ applicationId: persistedId }));
          return true;
        }

        // Nothing resolved yet (first login, or a deep link in a fresh browser)
        // — /loading looks up their applications and sends them back here.
        return this.router.parseUrl('/loading');
      })
    );
  }

  ///end of guard
}
